/**
 * useDateValidation - Date conflict validation utilities
 *
 * Validates basic timeline consistency (e.g. due not before start).
 * Estimated duration is a planning guide only — it does not block due dates or edits.
 */

import {
  getEffectiveDurationMinutes,
  MIN_DURATION_MINUTES,
} from '../../../../../utils/taskDuration';
import { parseMissionDate } from '../../../../../utils/missionDates';

function toValidationDate(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return value;
  return parseMissionDate(value) ?? new Date(value);
}

/**
 * Validate a due date change
 * @param {Object} task - The task being modified
 * @param {string|Date} newDueDate - The proposed new due date
 * @returns {{ valid: boolean, error?: { title: string, message: string } }}
 */
export function validateDueDateChange(task, newDueDate) {
  if (!newDueDate) return { valid: true }; // Clearing due date is always allowed

  const dueDate = toValidationDate(newDueDate);

  const startDate = task.startedAt ? toValidationDate(task.startedAt)
    : task.createdAt ? toValidationDate(task.createdAt) : null;

  if (!startDate) return { valid: true };

  if (dueDate < startDate) {
    return {
      valid: false,
      error: {
        title: "That won't work",
        message: "You're trying to set a deadline that's before the mission even started. The due date needs to be after the start date — otherwise, the mission would be overdue before it begins!",
      },
    };
  }

  return { valid: true };
}

/**
 * Validate a start date change
 * @param {Object} task - The task being modified
 * @param {string|Date} newStartDate - The proposed new start date
 * @returns {{ valid: boolean, error?: { title: string, message: string } }}
 */
export function validateStartDateChange(task, newStartDate) {
  if (!newStartDate) return { valid: true };

  const startDate = toValidationDate(newStartDate);

  if (task.dueDate) {
    const dueDate = toValidationDate(task.dueDate);

    if (startDate > dueDate) {
      return {
        valid: false,
        error: {
          title: "That won't work",
          message: "You're trying to start this mission after its deadline has already passed. That doesn't make sense — you'd need to either move the start date earlier, or push the due date out further.",
        },
      };
    }
  }

  return { valid: true };
}

/**
 * Validate an estimated duration change (minutes)
 * @param {Object} task - The task being modified
 * @param {number} newMinutes - The proposed new duration in minutes
 * @returns {{ valid: boolean, error?: { title: string, message: string } }}
 */
export function validateEstimatedDurationChange(_task, _newMinutes) {
  return { valid: true };
}

/**
 * Validate a Gantt drag operation
 * @param {Object} task
 * @param {Date|null} newStartDate
 * @param {number} third - delta minutes (move, resize-start) or new total minutes (resize-end)
 * @param {string} dragType - 'move' | 'resize-start' | 'resize-end'
 */
export function validateGanttDrag(task, newStartDate, third, dragType) {
  if (dragType === 'move') {
    if (newStartDate) {
      return validateStartDateChange(task, newStartDate);
    }
  } else if (dragType === 'resize-start') {
    const deltaMinutes = third;
    const oldMinutes = getEffectiveDurationMinutes(task) || MIN_DURATION_MINUTES;
    const newMinutes = Math.max(MIN_DURATION_MINUTES, oldMinutes - deltaMinutes);
    const tempTask = { ...task, estimatedDurationMinutes: newMinutes };
    if (newStartDate) {
      return validateStartDateChange(tempTask, newStartDate);
    }
  } else if (dragType === 'resize-end') {
    return validateEstimatedDurationChange(task, third);
  }

  return { valid: true };
}

const dateValidation = {
  validateDueDateChange,
  validateStartDateChange,
  validateEstimatedDurationChange,
  validateGanttDrag,
};

export default dateValidation;
